import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DataResponse } from '../../models/data-response';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private http = inject(HttpClient);

  // The CSRF marker and Okta bearer token are attached by authInterceptor.
  // This service is only responsible for request shape and response mapping.
  private readonly urlEncodedHeader = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  getRequest<T>(url: string, rawResponse = false, context?: HttpContext): Observable<T> {
    return rawResponse
      ? this.http.get<T>(url, { context })
      : this.http.get<DataResponse<T>>(url, { context }).pipe(map((response) => response.result));
  }

  postRequest<T>(
    url: string,
    body: unknown,
    encode = false,
    withCredentials = false,
    rawResponse = false,
    context?: HttpContext,
  ): Observable<T> {
    const options = {
      headers: encode ? this.urlEncodedHeader : undefined,
      withCredentials,
      context,
    };

    return rawResponse
      ? this.http.post<T>(url, body, options)
      : this.http
          .post<DataResponse<T>>(url, body, options)
          .pipe(map((response) => response.result));
  }

  /**
   * POSTs to an endpoint whose success body is a **file**, not a `DataResponse<T>` envelope
   * (e.g. the audit-log CSV export). The full `HttpResponse` is surfaced rather than just the
   * body because the response headers carry the download's identity — `Content-Disposition`
   * (file name) and `Content-Type` — which a body-only observable throws away.
   *
   * The body is a `Blob`, so an error response is a `Blob` too: a caller that wants to read a
   * JSON error payload has to convert `HttpErrorResponse.error` back to text itself.
   *
   * `context` is forwarded to the interceptor chain — pass one from
   * `suppressGlobalErrorNavigation()` when the failure should be handled in place rather than
   * sending the user to `/error`.
   */
  postFileRequest(
    url: string,
    body: unknown,
    headers?: HttpHeaders,
    context?: HttpContext,
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(url, body, {
      headers,
      context,
      observe: 'response',
      responseType: 'blob',
    });
  }

  deleteRequest<T>(url: string, context?: HttpContext): Observable<T> {
    return this.http
      .delete<DataResponse<T>>(url, { context })
      .pipe(map((response) => response.result));
  }

  putRequest<T>(url: string, body: unknown, encode = false, context?: HttpContext): Observable<T> {
    const options = { headers: encode ? this.urlEncodedHeader : undefined, context };

    return this.http
      .put<DataResponse<T>>(url, body, options)
      .pipe(map((response) => response.result));
  }
}
